/**
 * ChatMessageList — Renders chat messages with special cards.
 * Brief cards, skill result cards, normal messages.
 *
 * Enhanced features (via sub-components):
 * - Artifact rendering: html/jsx/tsx/svg code blocks → sandboxed preview
 * - Action chips: ---ACTION: tags → styled colored badges
 * - Streaming indicator: animated dots while status='thinking'|'answering'
 * - Message copy button: copy icon on hover for each message
 * - SkillResultCard: rich media rendering for __SKILL_RESULT__: prefixed messages
 *
 * Rule 94: Modüler — mesaj render ayrı component.
 */
import { CheckSquare, ClipboardList, Copy, Edit3, Loader2, Play, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { ChatMessage } from '../../../contexts/ChatContext'
import { ActionChip, ArtifactRenderer, StreamingDots } from './ChatMessageParts'
import { extractArtifacts, parseActionTags } from './ChatMessageUtils'
import type { SkillResultCardProps } from './SkillResultCard'
import { SkillResultCard } from './SkillResultCard'

interface SubAgentPayload {
  name: string
  status: 'running' | 'done' | 'failed'
  task: string
  result?: string
}

interface GenerativeUIPayload {
  type: 'task_list' | 'file_list' | 'stats' | 'weather' | 'links'
  title?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
}

interface BriefPayload {
  brief_id: number
  title: string
  tasks: Array<{ title: string; complexity?: number; description?: string; priority?: string }>
  estimated_hours?: number
  goals?: string[]
}

interface Props {
  chat: ChatMessage[]
  isHorizontal: boolean
  briefStates: Record<number, 'pending' | 'approved' | 'editing'>
  onApproveBrief: (briefId: number, title: string) => void
}

export default function ChatMessageList({ chat, isHorizontal, briefStates, onApproveBrief }: Props) {
  const messagesRef = useRef<HTMLDivElement>(null)
  const [expandedSkills, setExpandedSkills] = useState<Set<number>>(new Set())

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight })
  }, [chat])

  if (chat.length === 0) {
    return (
      <div ref={messagesRef} style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
        <Sparkles size={20} style={{ opacity: 0.3, marginBottom: 8 }} />
        <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>Send a message to start</div>
      </div>
    )
  }

  return (
    <div
      ref={messagesRef}
      style={{
        flex: 1, overflowY: 'auto', padding: '10px 12px',
        display: 'flex', flexDirection: isHorizontal ? 'row' : 'column',
        gap: 8, minHeight: 0,
      }}
    >
      {chat.map((m, i) => {
        if (m.role === 'assistant' && typeof m.content === 'string' && m.content.startsWith('__BRIEF__:')) {
          return <BriefCard key={i} content={m.content} briefStates={briefStates} onApprove={onApproveBrief} isHorizontal={isHorizontal} />
        }
        // Rich skill result card: __SKILL_RESULT__:{"skillName":"...","resultType":"image","result":"...","query":"..."}
        if (m.role === 'assistant' && typeof m.content === 'string' && m.content.startsWith('__SKILL_RESULT__:')) {
          try {
            const payload = JSON.parse(m.content.slice('__SKILL_RESULT__:'.length)) as SkillResultCardProps
            return <SkillResultCard key={i} {...payload} />
          } catch { /* fall through to SkillCard */ }
        }
        // Sub-agent tracking card: __SUBAGENT__:{"name":"...","status":"running|done|failed","task":"..."}
        if (m.role === 'assistant' && typeof m.content === 'string' && m.content.startsWith('__SUBAGENT__:')) {
          try {
            const payload = JSON.parse(m.content.slice('__SUBAGENT__:'.length)) as SubAgentPayload
            return <SubAgentCard key={i} payload={payload} />
          } catch { /* fall through */ }
        }
        // Generative UI: __COMPONENT__:{"type":"task_list"|"file_list"|"weather"|"stats","data":{...}}
        if (m.role === 'assistant' && typeof m.content === 'string' && m.content.startsWith('__COMPONENT__:')) {
          try {
            const payload = JSON.parse(m.content.slice('__COMPONENT__:'.length)) as GenerativeUIPayload
            return <GenerativeUICard key={i} payload={payload} />
          } catch { /* fall through */ }
        }
        if (m.role === 'assistant' && m.agentName === 'Skill Engine') {
          return <SkillCard key={i} message={m} index={i} isHorizontal={isHorizontal} expandedSkills={expandedSkills} setExpandedSkills={setExpandedSkills} />
        }
        return <NormalMessage key={i} message={m} isHorizontal={isHorizontal} />
      })}
    </div>
  )
}

/* ── Brief Card ── */
function BriefCard({ content, briefStates, onApprove, isHorizontal }: {
  content: string; briefStates: Record<number, string>; onApprove: (id: number, title: string) => void; isHorizontal: boolean
}) {
  let payload: BriefPayload | null = null
  try { payload = JSON.parse(content.slice('__BRIEF__:'.length)) } catch { /* */ }
  if (!payload) return null

  const state = briefStates[payload.brief_id] ?? 'pending'
  const locked = state === 'approved'

  return (
    <div style={{
      alignSelf: 'flex-start', maxWidth: isHorizontal ? 420 : '95%',
      background: 'var(--os-card)', borderRadius: 12, padding: 12,
      border: '1px solid var(--os-border)', borderLeft: '3px solid #6366f1',
      fontSize: 11, lineHeight: 1.5, color: 'var(--os-text)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <ClipboardList size={14} style={{ color: '#6366f1' }} />
        <span style={{ fontWeight: 700, fontSize: 12 }}>Brief: {payload.title}</span>
      </div>
      <div style={{ fontSize: 10, color: 'var(--os-muted)', marginBottom: 8 }}>
        {payload.tasks.length} tasks created{payload.estimated_hours ? ` · ~${payload.estimated_hours}h` : ''}
      </div>
      {payload.goals && payload.goals.length > 0 && (
        <div style={{ fontSize: 10, color: 'var(--os-muted)', marginBottom: 6, lineHeight: 1.4 }}>
          🎯 {payload.goals.slice(0, 2).join(' · ')}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
        {payload.tasks.map((t, ti) => (
          <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5 }}>
            <CheckSquare size={11} style={{ color: 'var(--os-muted)', flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{t.title}</span>
            {typeof t.complexity === 'number' && (
              <span style={{ fontSize: 9, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>
                c:{t.complexity}
              </span>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button disabled={locked} onClick={() => onApprove(payload!.brief_id, payload!.title)} style={{
          flex: 1, padding: '5px 10px', borderRadius: 6,
          background: locked ? 'var(--os-hover)' : '#10b981',
          color: locked ? 'var(--os-muted)' : '#fff',
          border: 'none', cursor: locked ? 'default' : 'pointer',
          fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        }}>
          <Play size={11} /> {locked ? 'Approved' : 'Approve & Start'}
        </button>
        <button disabled={locked} onClick={() => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: { id: 'task-manager' } }))} style={{
          padding: '5px 10px', borderRadius: 6,
          background: 'var(--os-hover)', color: 'var(--os-text)',
          border: 'none', cursor: locked ? 'default' : 'pointer',
          fontSize: 10.5, fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <Edit3 size={11} /> Edit
        </button>
      </div>
    </div>
  )
}

/* ── Skill Result Card ── */
function SkillCard({ message, index, isHorizontal, expandedSkills, setExpandedSkills }: {
  message: ChatMessage; index: number; isHorizontal: boolean
  expandedSkills: Set<number>; setExpandedSkills: React.Dispatch<React.SetStateAction<Set<number>>>
}) {
  const lines = message.content.split('\n')
  const headerLine = lines[0]
  const resultText = lines.slice(1).join('\n').trim()
  const isExpanded = expandedSkills.has(index)
  const displayText = isExpanded ? resultText : resultText.slice(0, 300) + (resultText.length > 300 ? '...' : '')

  return (
    <div style={{
      alignSelf: 'flex-start', maxWidth: isHorizontal ? 400 : '95%',
      borderLeft: '3px solid #ec4899', background: 'var(--os-card)',
      borderRadius: 8, padding: 10, border: '1px solid var(--os-border)',
      fontSize: 11, lineHeight: 1.5, color: 'var(--os-text)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 11 }}>{headerLine}</span>
        <button onClick={() => navigator.clipboard.writeText(resultText)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--os-muted)', padding: 0, flexShrink: 0, marginLeft: 8,
        }} title="Copy result">
          <Copy size={12} />
        </button>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', marginBottom: resultText.length > 300 ? 6 : 0 }}>{displayText}</div>
      {resultText.length > 300 && (
        <button onClick={() => {
          setExpandedSkills(prev => { const next = new Set(prev); if (next.has(index)) next.delete(index); else next.add(index); return next })
        }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--os-accent)', fontSize: 10, fontWeight: 600, padding: 0 }}>
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

/* ── Agent Style Helper ── */
interface AgentStyle {
  bg: string
  border: string
  accent: string
  emoji: string
  label: string
}

function getAgentStyle(agentName?: string): AgentStyle | null {
  if (!agentName) return null
  if (agentName === 'Anne Filiz') return { bg: 'rgba(236,72,153,0.08)', border: 'rgba(236,72,153,0.25)', accent: '#ec4899', emoji: '🌸', label: 'Anne Filiz' }
  if (agentName === 'Baba Filiz') return { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)', accent: '#3b82f6', emoji: '🌊', label: 'Baba Filiz' }
  if (agentName.includes('Expert') || agentName.includes('expert')) return { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', accent: '#f59e0b', emoji: '⭐', label: agentName }
  if (agentName === 'Gemini') return { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)', accent: '#3b82f6', emoji: 'G', label: 'Gemini' }
  if (agentName === 'Claude') return { bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.25)', accent: '#f97316', emoji: 'C', label: 'Claude' }
  if (agentName === 'Pulse' || agentName === 'System') return { bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.2)', accent: 'var(--os-accent)', emoji: '💡', label: 'RunAI' }
  return null
}

/* ── Normal Message ── */
function NormalMessage({ message: m, isHorizontal }: { message: ChatMessage; isHorizontal: boolean }) {
  const [hovered, setHovered] = useState(false)
  const [copied, setCopied] = useState(false)

  const isStreaming = m.status === 'thinking' || m.status === 'answering'
  const { text: textWithoutActions, actions } = parseActionTags(m.content)
  const { text: displayText, artifacts } = extractArtifacts(textWithoutActions)

  const eqEmoji = m.eq_score ? (m.eq_score >= 8 ? '✦' : m.eq_score >= 6 ? '◆' : m.eq_score >= 4 ? '◇' : '○') : null
  const eqColor = m.eq_score ? (m.eq_score >= 8 ? '#10b981' : m.eq_score >= 6 ? '#6366f1' : m.eq_score >= 4 ? '#f59e0b' : '#ef4444') : null

  // Special agent card for assistant messages
  const agentStyle = m.role === 'assistant' ? getAgentStyle(m.agentName) : null
  const isProactive = m.role === 'assistant' && !agentStyle && (
    m.message_type === 'proactive' ||
    m.message_type === 'briefing' ||
    m.message_type === 'alert' ||
    (typeof m.content === 'string' && (m.content.startsWith('🌅') || m.content.startsWith('⏰') || m.content.startsWith('📅')))
  )
  const proactiveEmoji = m.message_type === 'alert' ? '⏰' : m.message_type === 'briefing' ? '📅' : '🌅'
  const proactiveLabel = m.message_type === 'alert' ? 'Deadline Alert' : m.message_type === 'briefing' ? 'Morning Briefing' : 'RunAI'
  const proactiveStyle: AgentStyle | null = isProactive
    ? { bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.2)', accent: 'var(--os-accent)', emoji: proactiveEmoji, label: proactiveLabel }
    : null
  const activeStyle = agentStyle ?? proactiveStyle

  if (activeStyle) {
    return (
      <div
        style={{ alignSelf: 'flex-start', maxWidth: isHorizontal ? 340 : '85%', position: 'relative', flexShrink: 0 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Copy button on hover */}
        {hovered && !isStreaming && (
          <button onClick={() => { navigator.clipboard.writeText(m.content); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
            title="Copy message"
            style={{
              position: 'absolute', top: -8, left: 0, zIndex: 10,
              background: 'var(--os-surface)', border: '1px solid var(--os-border)',
              borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 3,
              color: copied ? '#10b981' : 'var(--os-muted)',
              fontSize: 9.5, fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            <Copy size={10} /> {copied ? 'Copied' : 'Copy'}
          </button>
        )}

        {/* Agent card */}
        <div style={{
          background: activeStyle.bg,
          border: `1px solid ${activeStyle.border}`,
          borderLeft: `3px solid ${activeStyle.accent}`,
          borderRadius: 10,
          padding: '8px 12px',
          marginBottom: 4,
          opacity: m.status === 'thinking' ? 0.6 : 1,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: activeStyle.accent, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{activeStyle.emoji}</span>
            <span>{activeStyle.label}</span>
            {eqEmoji && m.eq_score && (
              <span title={`EQ ${m.eq_score.toFixed(1)}`} style={{ fontSize: 9, color: eqColor ?? undefined, fontWeight: 700, marginLeft: 'auto' }}>
                {eqEmoji} {m.eq_score.toFixed(1)}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--os-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {displayText}
            {isStreaming && <StreamingDots />}
          </div>
          {actions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
              {actions.map((a, ai) => <ActionChip key={ai} type={a.type} params={a.params} />)}
            </div>
          )}
        </div>

        {/* Artifacts below card */}
        {artifacts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            {artifacts.map((artifact, ai) => <ArtifactRenderer key={ai} artifact={artifact} />)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: isHorizontal ? 320 : '90%', position: 'relative', flexShrink: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Copy button on hover */}
      {hovered && !isStreaming && (
        <button onClick={() => { navigator.clipboard.writeText(m.content); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
          title="Copy message"
          style={{
            position: 'absolute', top: -8,
            right: m.role === 'user' ? 0 : 'auto',
            left: m.role === 'assistant' ? 0 : 'auto',
            zIndex: 10,
            background: 'var(--os-surface)', border: '1px solid var(--os-border)',
            borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 3,
            color: copied ? '#10b981' : 'var(--os-muted)',
            fontSize: 9.5, fontFamily: 'inherit',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <Copy size={10} /> {copied ? 'Copied' : 'Copy'}
        </button>
      )}

      {/* Bubble */}
      <div style={{
        padding: '7px 11px',
        borderRadius: m.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
        background: m.role === 'user' ? 'var(--os-bubble-user)' : 'var(--os-card)',
        border: m.role === 'user' ? 'none' : '1px solid var(--os-border)',
        fontSize: 11, lineHeight: 1.5,
        color: m.role === 'user' ? 'var(--os-bubble-text)' : 'var(--os-text)',
        opacity: m.status === 'thinking' ? 0.6 : 1,
      }}>
        {m.role === 'assistant' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            {m.agentName && <span style={{ fontSize: 9, color: 'var(--os-accent)', fontWeight: 600 }}>{m.agentName}</span>}
            {eqEmoji && m.eq_score && (
              <span title={`EQ ${m.eq_score.toFixed(1)}`} style={{ fontSize: 9, color: eqColor ?? undefined, fontWeight: 700, marginLeft: 'auto' }}>
                {eqEmoji} {m.eq_score.toFixed(1)}
              </span>
            )}
          </div>
        )}
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {displayText}
          {isStreaming && <StreamingDots />}
        </div>
        {actions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {actions.map((a, ai) => <ActionChip key={ai} type={a.type} params={a.params} />)}
          </div>
        )}
      </div>

      {/* Artifacts below bubble */}
      {artifacts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          {artifacts.map((artifact, ai) => <ArtifactRenderer key={ai} artifact={artifact} />)}
        </div>
      )}
    </div>
  )
}

/* ── Sub-Agent Tracking Card ── */
function SubAgentCard({ payload }: { payload: SubAgentPayload }) {
  const statusColor = payload.status === 'done' ? '#10b981' : payload.status === 'failed' ? '#ef4444' : '#f59e0b'
  const statusBg = payload.status === 'done' ? 'rgba(16,185,129,0.08)' : payload.status === 'failed' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)'
  const statusBorder = payload.status === 'done' ? 'rgba(16,185,129,0.25)' : payload.status === 'failed' ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'

  return (
    <div style={{
      alignSelf: 'flex-start', maxWidth: '90%',
      background: statusBg,
      border: `1px solid ${statusBorder}`,
      borderLeft: `3px solid ${statusColor}`,
      borderRadius: 8, padding: '7px 10px',
      fontSize: 11, lineHeight: 1.5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: payload.result ? 4 : 0 }}>
        {payload.status === 'running'
          ? <Loader2 size={11} style={{ color: statusColor, animation: 'spin 1s linear infinite', flexShrink: 0 }} />
          : <span style={{ fontSize: 11, flexShrink: 0 }}>{payload.status === 'done' ? '✓' : '✗'}</span>
        }
        <span style={{ fontWeight: 600, color: statusColor, fontSize: 10 }}>
          {payload.name}
        </span>
        <span style={{ color: 'var(--os-muted)', fontSize: 10, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {payload.task}
        </span>
      </div>
      {payload.result && (
        <div style={{ fontSize: 10.5, color: 'var(--os-text)', marginTop: 2, whiteSpace: 'pre-wrap' }}>
          {payload.result.slice(0, 200)}{payload.result.length > 200 ? '...' : ''}
        </div>
      )}
    </div>
  )
}

/* ── Generative UI Card (COPILOT-5) ── */
function GenerativeUICard({ payload }: { payload: GenerativeUIPayload }) {
  const { type, title, data } = payload

  if (type === 'task_list' && Array.isArray(data)) {
    return (
      <div style={{ alignSelf: 'flex-start', maxWidth: '90%', background: 'var(--os-card)', border: '1px solid var(--os-border)', borderLeft: '3px solid var(--os-accent)', borderRadius: 10, padding: '8px 12px', fontSize: 11 }}>
        {title && <div style={{ fontWeight: 700, color: 'var(--os-accent)', marginBottom: 6, fontSize: 11 }}>📋 {title}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.slice(0, 8).map((task: { title?: string; status?: string; priority?: string }, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
              <span style={{ fontSize: 10 }}>{task.status === 'done' ? '✅' : task.status === 'in_progress' ? '🔄' : '⏳'}</span>
              <span style={{ flex: 1, color: 'var(--os-text)' }}>{task.title}</span>
              {task.priority && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 8, background: task.priority === 'urgent' ? 'rgba(239,68,68,0.15)' : 'var(--os-hover)', color: task.priority === 'urgent' ? '#ef4444' : 'var(--os-muted)' }}>{task.priority}</span>}
            </div>
          ))}
          {data.length > 8 && <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>+{data.length - 8} more</div>}
        </div>
        <button onClick={() => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'task-manager' }))}
          style={{ marginTop: 8, fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--os-border)', background: 'var(--os-hover)', color: 'var(--os-muted)', cursor: 'pointer', fontFamily: 'inherit' }}>
          Open TaskManager →
        </button>
      </div>
    )
  }

  if (type === 'file_list' && Array.isArray(data)) {
    return (
      <div style={{ alignSelf: 'flex-start', maxWidth: '90%', background: 'var(--os-card)', border: '1px solid var(--os-border)', borderLeft: '3px solid #10b981', borderRadius: 10, padding: '8px 12px', fontSize: 11 }}>
        {title && <div style={{ fontWeight: 700, color: '#10b981', marginBottom: 6, fontSize: 11 }}>📁 {title}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {data.slice(0, 6).map((file: { name?: string; size?: number; folder?: string }, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
              <span style={{ fontSize: 10 }}>📄</span>
              <span style={{ flex: 1, color: 'var(--os-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
              {file.size && <span style={{ fontSize: 9, color: 'var(--os-muted)' }}>{Math.round(file.size / 1024)}KB</span>}
            </div>
          ))}
        </div>
        <button onClick={() => window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-files' }))}
          style={{ marginTop: 8, fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--os-border)', background: 'var(--os-hover)', color: 'var(--os-muted)', cursor: 'pointer', fontFamily: 'inherit' }}>
          Open RunFiles →
        </button>
      </div>
    )
  }

  if (type === 'stats' && typeof data === 'object') {
    const entries = Object.entries(data as Record<string, string | number>)
    return (
      <div style={{ alignSelf: 'flex-start', maxWidth: '90%', background: 'var(--os-card)', border: '1px solid var(--os-border)', borderLeft: '3px solid #8b5cf6', borderRadius: 10, padding: '8px 12px', fontSize: 11 }}>
        {title && <div style={{ fontWeight: 700, color: '#8b5cf6', marginBottom: 6, fontSize: 11 }}>📊 {title}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {entries.slice(0, 6).map(([key, val], i) => (
            <div key={i} style={{ background: 'var(--os-surface)', borderRadius: 6, padding: '5px 8px' }}>
              <div style={{ fontSize: 9, color: 'var(--os-muted)', marginBottom: 2 }}>{key}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--os-text)' }}>{String(val)}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'links' && Array.isArray(data)) {
    return (
      <div style={{ alignSelf: 'flex-start', maxWidth: '90%', background: 'var(--os-card)', border: '1px solid var(--os-border)', borderLeft: '3px solid #3b82f6', borderRadius: 10, padding: '8px 12px', fontSize: 11 }}>
        {title && <div style={{ fontWeight: 700, color: '#3b82f6', marginBottom: 6, fontSize: 11 }}>🔗 {title}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.slice(0, 5).map((link: { title?: string; url?: string; desc?: string }, i: number) => (
            <button key={i} onClick={() => {
              window.dispatchEvent(new CustomEvent('runai:browser-navigate', { detail: { url: link.url } }))
              window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-browser' }))
            }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, padding: '5px 8px', borderRadius: 6, background: 'var(--os-surface)', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
              <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>{link.title || link.url}</span>
              {link.desc && <span style={{ fontSize: 10, color: 'var(--os-muted)' }}>{link.desc.slice(0, 80)}</span>}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Fallback: JSON display
  return (
    <div style={{ alignSelf: 'flex-start', maxWidth: '90%', background: 'var(--os-card)', border: '1px solid var(--os-border)', borderRadius: 8, padding: '8px 12px', fontSize: 10 }}>
      {title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>}
      <pre style={{ margin: 0, color: 'var(--os-muted)', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{JSON.stringify(data, null, 2).slice(0, 300)}</pre>
    </div>
  )
}
